import { Helmet } from 'react-helmet-async';
import { useState, useContext, useEffect, useCallback } from 'react';
import {useNavigate} from 'react-router-dom';
// @mui
import {
  TextField,
  Card,
  Stack,
  Button,
  Container,
  Typography,
  IconButton,
  Grid,
  Box,
  Divider,
  Paper,
} from '@mui/material';
// components
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import Iconify from '../components/iconify';
import Label from '../components/label';

import { EntityContext } from '../entity';

const FinalWebhookList = ({ webhookList, handleDeleteWebhook, ...other }) => {
  return (
    <Grid container spacing={2}>
      {webhookList.map((configuration) => (
        <Grid item xs={12} sm={6} md={4} key={configuration.configuration_key}>
          <Card 
            sx={{ 
              p: 2, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              '&:hover': {
                boxShadow: 4,
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <IconButton 
                size="small" 
                color="error" 
                onClick={() => handleDeleteWebhook(configuration.configuration_key)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
            
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                  URL
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    backgroundColor: 'action.hover',
                    p: 1,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}
                >
                  {configuration.url}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                  Headers
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    backgroundColor: 'action.hover',
                    p: 1,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: '150px',
                    overflow: 'auto'
                  }}
                >
                  {typeof configuration.headers === 'string' 
                    ? configuration.headers 
                    : JSON.stringify(configuration.headers, null, 2)}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                  KEY
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    backgroundColor: 'action.hover',
                    p: 1,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}
                >
                  {configuration.configuration_key}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

const WebhookList = ({webhookList, handleDeleteWebhook, ...other}) => {
  return (
    <>
      { webhookList.length === 0 ? 
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma configuração cadastrada
          </Typography>
        </Box>
       : <FinalWebhookList webhookList={webhookList} handleDeleteWebhook={handleDeleteWebhook}/> }
    </>
  )
}

const AddWebhookCard = (props) => {
  return (
    <Stack justifyContent="space-between" mb={5}>
        <Stack direction="row" alignItems="center" justifyContent="center" useFlexGap flexWrap="wrap">
          <TextField sx={{ m: 2 }}
            id="outlined-basic" 
            label="URL" 
            variant="outlined"
            value={props.url}
            onChange={e => props.setUrl(e.target.value)}/>
          <TextField sx={{ m: 2 }}
            id="outlined-basic" 
            label="Headers" 
            variant="outlined"
            value={props.headers}
            onChange={e => props.setHeaders(e.target.value)}/>
          <Button 
          sx={{ m: 2 }} 
          variant="contained" 
          component="label" 
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={_ => props.handleAddWebhook()}>
          Adicionar
          </Button>
        </Stack>
    </Stack>
  )
}

export default function IntegrationPage() {

  const [isVisible, setIsVisible] = useState(false)
  const [addWebhook, setAddWebhook] = useState(false)
  const [webhookList, setWebhookList] = useState([])

  const [url, setUrl] = useState('')
  const [headers, setHeaders] = useState('')

  const { entity, getWebhooks, createWebhook, deleteWebhook } = useContext(EntityContext)

  const navigate = useNavigate();

  const handleNavigateToPlans = () => {
      navigate('/dashboard/plan/management');
  };
  

  useEffect(() => {
    getConfigurationsList()
    }, [])

    const getConfigurationsList = useCallback(async () => {
	
      getWebhooks()
          .then((data) => {
            
            setWebhookList(data.configurations)
          })
      
        }, [])

  const handleIsVisible = () => {
    setIsVisible(!isVisible)
  }

  const handleAddWebhook = () => {
    createWebhook(url, headers).then(
      (res) => {
        setAddWebhook(!addWebhook)
        setUrl('')
        setHeaders('')
        getConfigurationsList()
      }
    )
  }

  const handleDeleteWebhook = (webhookConfigurationKey) => {
    deleteWebhook(webhookConfigurationKey).then(
      (res) => {
        getConfigurationsList()
      }
    )
  }

  const handleShowAddWebhook = () => {
    setAddWebhook(!addWebhook)
  }

  return (
    <>
      <Helmet>
        <title> Configuração </title>
      </Helmet>

      <Container >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Configuração
          </Typography>
        </Stack>

        <Card >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.2} sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Informações
            </Typography>
          </Stack>
          <Stack spacing={2} sx={{ p: 3 }}>
            <Typography variant="subtitle2" noWrap>
              Nome: {entity.entity_name}
            </Typography>
            <Typography variant="subtitle2" noWrap>
              Numero de documento: {entity.entity_document_number}
            </Typography>
            <Typography variant="subtitle2" noWrap>
              KEY: {entity.entity_key}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.2} >
            <Typography variant="subtitle2" noWrap>
              Plano: <Label>{entity.plan.name}</Label>
            </Typography>
            <Button onClick={handleNavigateToPlans}>Upgrade/Downgrade</Button>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.2}  sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Integrações
            </Typography>
          </Stack>
          { entity.plan.api_allowed ?
          
            <Stack spacing={2} sx={{ p: 3 }}  direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" noWrap>
                API KEY:   { isVisible ? entity.api_key : "********************"}
              </Typography>
              <Stack direction="row" spacing={2}>
              <IconButton size="small" color="inherit" onClick={_ => handleIsVisible()}>
                { isVisible ? <VisibilityOffIcon /> : <VisibilityIcon /> }
              </IconButton>
              </Stack>
            </Stack>

            :

            <Stack spacing={2} sx={{ p: 3 }}  direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" noWrap>
                Este Plano Não Contempla o Uso de APIs.
              </Typography>
            </Stack>
        
          }

          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.2}  sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Webhooks
            </Typography>
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={_ => handleShowAddWebhook()}>
              Novo Webhook
            </Button>
          </Stack>

          { addWebhook ? < AddWebhookCard url={url} headers={headers} setHeaders={setHeaders} setUrl={setUrl} handleAddWebhook={handleAddWebhook}/> : <></> }

          <Stack spacing={2} sx={{ p: 3 }}  direction="column" justifyContent="space-between">
          <WebhookList webhookList={webhookList} handleDeleteWebhook={handleDeleteWebhook}/>
          </Stack>
        </Card>
      </Container>
    </>
  );
}
